import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, NonNullableFormBuilder, Validators } from '@angular/forms';
import { SalesTaxInfo, SalesTaxModalService } from '@cargos/sprintpay-ui';
import { CustomMasks, CustomValidators } from '@cargos/sprintpay-utils';
import { EMPTY, Subject, finalize, of, switchMap, take, takeUntil } from 'rxjs';
import { ThemeType } from 'src/app/shared/models';
import { Shipment } from 'src/app/shared/models/shipment';

import { Router } from '@angular/router';
import { TaxAddressPayload } from '@cargos/sprintpay-services';
import { cleanDollarMask } from '@cargos/sprintpay-tools';
import dayjs from 'dayjs';
import { FormErrorJSON } from 'src/app/shared/models/form-errors';
import {
    AwbLookUpService,
    ConfigService,
    CurrentFacilityService,
    CustomerService,
    PaymentRequestService,
    ThemeService,
} from 'src/app/shared/services';
import { BannersService } from 'src/app/shared/services/banners.service';
import { TOUNotification } from 'src/app/shared/utils/notifications';
import Swal from 'sweetalert2';
import { BDPSearchFormGroupType, BDPSearchFormKeysType } from '../../models/search-form';
import { formSearchAWBErrors } from './utils/form-errors';

@Component({
    selector: 'app-search-awb',
    templateUrl: './search-awb.component.html',
    styleUrls: ['./search-awb.component.scss'],
    standalone: false,
})
export class SearchAwbComponent implements OnInit, OnDestroy {
    private unsubscribe$ = new Subject<void>();
    public bdpImages: string[];
    public ThemeType = ThemeType;
    public currentTheme: ThemeType;
    public searchForm: BDPSearchFormGroupType;
    public isLoading = false;
    public formError: FormErrorJSON<BDPSearchFormKeysType>;
    public maskAmount = CustomMasks.iMaskAmount;
    private source = '';
    private dateFormat = 'YYYY-MM-DD';
    private taxEligible: boolean = false;

    @Input() disabled = false;
    constructor(
        private router: Router,
        private awbLookUpService: AwbLookUpService,
        private configService: ConfigService,
        private paymentRequestService: PaymentRequestService,
        private fb: NonNullableFormBuilder,
        private currentFacilityService: CurrentFacilityService,
        private themeService: ThemeService,
        private customerService: CustomerService,
        private salesTaxModalService: SalesTaxModalService,
        private bannerService: BannersService
    ) {
        this.bdpImages = ['assets/bdp/img/home-bdp-01.webp'];

        this.source = this.currentFacilityService.getFacilitySource();
        this.subscribeTheme();
    }

    ngOnInit(): void {
        this.bannerService.setAndSaveTouBanner(TOUNotification);
        this.createForm();
        this.setFormError();
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    get hawb(): AbstractControl<string> | null {
        return this.searchForm.get('referenceNumber');
    }

    get awb(): AbstractControl<string> | null {
        return this.searchForm.get('awb');
    }

    get amount(): AbstractControl<string> | null {
        return this.searchForm.get('amount');
    }

    createForm(): void {
        this.searchForm = this.fb.group({
            awb: ['', [Validators.required, Validators.maxLength(100)]],
            hawbId: ['', [Validators.required, Validators.maxLength(100)]],
            amount: [
                '',
                [
                    Validators.required,
                    CustomValidators.minAmountValidator(this.configService.getMinimumAmountAllowed()),
                ],
            ],
        });
    }

    subscribeTheme(): void {
        this.themeService
            .getTheme()
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((theme) => (this.currentTheme = theme));
    }

    setFormError(): void {
        this.formError = {
            ...formSearchAWBErrors,
            amount: [
                ...formSearchAWBErrors.amount,
                {
                    code: 'min',
                    message: `Value of payment must be above $${this.configService.getMinimumAmountAllowed()}`,
                },
            ],
        };
    }

    saveShipment(billingInfo?: TaxAddressPayload): void {
        this.searchForm.markAllAsTouched();
        if (this.isLoading || this.searchForm.invalid) {
            return;
        }
        this.isLoading = true;
        const shipmentValue: Shipment = {
            ...this.searchForm.getRawValue(),
            paymentType: 'Invoice-Charges',
            amount: Number(cleanDollarMask(this.amount?.value || 0)),
            destination: 'bdp',
            pickupDate: dayjs().format(this.dateFormat),
            source: this.source,
            sourceExternalData: 'bdp',
        };
        this.awbLookUpService
            .isAllowedToAddCharges()
            .pipe(
                take(1),
                switchMap((isAllowed) => {
                    if (!isAllowed) {
                        return of({});
                    }

                    if (this.customerService.isTaxEligibleByUser() && !billingInfo) {
                        this.captureSalesTaxInfo();
                        this.isLoading = false;
                        return EMPTY;
                    }

                    return this.paymentRequestService.addShipmentToCart(shipmentValue, billingInfo).pipe(
                        take(1),
                        finalize(() => (this.isLoading = false))
                    );
                })
            )
            .subscribe({
                next: () => {
                    this.router.navigate([`/cart`]);
                },
                error: (error) => {
                    this.isLoading = false;

                    this.taxEligible = this.customerService.isTaxEligibleByUser(error);

                    if (this.taxEligible) {
                        this.captureSalesTaxInfo();
                        return;
                    }

                    if (error) {
                        Swal.fire({
                            title: error?.title,
                            text: error?.description,
                            icon: 'warning',
                            showConfirmButton: true,
                            confirmButtonText: 'Ok',
                            allowOutsideClick: false,
                        });
                    }
                },
            });
    }

    captureSalesTaxInfo(): void {
        this.salesTaxModalService
            .open()
            .pipe(
                take(1),
                switchMap((salesTaxInfo?: SalesTaxInfo) => {
                    return this.customerService.validateTaxEligibleResponse(this.taxEligible, salesTaxInfo);
                })
            )
            .subscribe((response) => {
                const { saveInfo, billingInfo } = response;

                if (!saveInfo) {
                    return;
                }

                this.taxEligible = false;
                this.saveShipment(billingInfo);
            });
    }
}
